import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PayoutManagement = ({
  currentBalance,
  minimumPayout,
  nextPayoutDate,
  onPayoutRequest
}) => {
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank-transfer');
  const [isScheduledPayout, setIsScheduledPayout] = useState(false);

  const paymentMethodOptions = [
    { value: 'bank-transfer', label: 'Bank Transfer' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'wire-transfer', label: 'Wire Transfer' },
    { value: 'crypto', label: 'Cryptocurrency' }
  ];

  const canRequestPayout = currentBalance >= minimumPayout;
  const maxPayoutAmount = currentBalance - (minimumPayout * 0.1); // Keep 10% of minimum as buffer

  const handlePayoutRequest = () => {
    if (payoutAmount && paymentMethod) {
      onPayoutRequest(parseFloat(payoutAmount), paymentMethod);
      setShowPayoutModal(false);
      setPayoutAmount('');
      setPaymentMethod('bank-transfer');
    }
  };

  const handleMaxAmount = () => {
    setPayoutAmount(maxPayoutAmount?.toFixed(2));
  };

  const getProcessingTime = (method) => {
    switch (method) {
      case 'bank-transfer':
        return '1-3 business days';
      case 'paypal':
        return 'Instant';
      case 'wire-transfer':
        return '1-2 business days';
      case 'crypto':
        return '15-30 minutes';
      default:
        return 'Variable';
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'bank-transfer':
        return 'Building2';
      case 'paypal':
        return 'CreditCard';
      case 'wire-transfer':
        return 'Zap';
      case 'crypto':
        return 'Bitcoin';
      default:
        return 'Wallet';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Payout Management</h3>
        <Icon name="Wallet" size={20} className="text-primary" />
      </div>
      {/* Balance Overview */}
      <div className="bg-background border border-border rounded-lg p-4 mb-6">
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-1">Available Balance</p>
          <p className="text-3xl font-bold text-primary mb-2">
            ${currentBalance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="AlertCircle" size={14} />
              <span>Min: ${minimumPayout?.toFixed(2)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>Next: {new Date(nextPayoutDate)?.toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Payout Actions */}
      <div className="space-y-4">
        <Button
          onClick={() => setShowPayoutModal(true)}
          disabled={!canRequestPayout}
          className="w-full"
          iconName="Send"
          iconPosition="left"
          iconSize={16}
        >
          Request Payout
        </Button>

        {!canRequestPayout && (
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <p className="text-sm text-warning">
                Minimum payout amount is ${minimumPayout?.toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* Scheduled Payout Toggle */}
        <div className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
          <div>
            <p className="text-sm font-medium text-foreground">Automatic Payouts</p>
            <p className="text-xs text-muted-foreground">Monthly automatic payouts when minimum is reached</p>
          </div>
          <button
            onClick={() => setIsScheduledPayout(!isScheduledPayout)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isScheduledPayout ? 'bg-primary' : 'bg-muted'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isScheduledPayout ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Payment Methods */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Saved Payment Methods</p>
          <div className="space-y-2">
            {paymentMethodOptions?.slice(0, 2)?.map((method) => (
              <div
                key={method?.value}
                className="flex items-center justify-between p-3 bg-background border border-border rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <Icon name={getMethodIcon(method?.value)} size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{method?.label}</p>
                    <p className="text-xs text-muted-foreground">
                      Processing: {getProcessingTime(method?.value)}
                    </p>
                  </div>
                </div>
                <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Request Payout</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPayoutModal(false)}
                iconName="X"
                iconSize={16}
              />
            </div>

            <div className="space-y-4">
              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Payout Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <input
                    type="number"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e?.target?.value)}
                    placeholder="0.00"
                    min={minimumPayout}
                    max={maxPayoutAmount}
                    step="0.01"
                    className="w-full pl-8 pr-16 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMaxAmount}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 text-xs"
                  >
                    MAX
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Available: ${maxPayoutAmount?.toFixed(2)}
                </p>
              </div>

              {/* Payment Method Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Payment Method
                </label>
                <Select
                  value={paymentMethod}
                  onValueChange={setPaymentMethod}
                  options={paymentMethodOptions}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Processing time: {getProcessingTime(paymentMethod)}
                </p>
              </div>

              {/* Summary */}
              <div className="bg-background border border-border rounded-lg p-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Processing Fee:</span>
                  <span className="text-foreground">$0.00</span>
                </div>
                <div className="flex justify-between items-center text-sm font-medium mt-1">
                  <span className="text-foreground">You'll receive:</span>
                  <span className="text-primary">${payoutAmount || '0.00'}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowPayoutModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePayoutRequest}
                  disabled={!payoutAmount || parseFloat(payoutAmount) < minimumPayout}
                >
                  Request Payout
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutManagement;